import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { PaginatedResponse } from '@/shared/types/api.types';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';

import type {
  CreateCustomerPayload,
  Customer,
  CustomerExportParams,
  CustomerExportResult,
  CustomerFilters,
  CustomerImportParams,
  CustomerImportResult,
  CustomerListParams,
  CustomerSearchParams,
  DeleteCustomerByCodeParams,
  GetCustomerByIdParams,
  Pro20Page,
  UpdateCustomerPayload,
} from '../types/customer.types';
import {
  buildCustomerFiltersCacheKey,
  DEFAULT_CUSTOMER_FILTERS,
  FORM_CODE_PARAM,
  MAX_EDGE_BOOKING_PERIOD_PARAM,
  MIN_EDGE_BOOKING_PERIOD_PARAM,
  NUMBERS_OF_BOOKINGS_PARAM,
} from '../utils/customer-filter-params';
import { normalizeCustomersResponse } from '../utils/normalize-customers';

function resolveFilters(filters?: CustomerFilters): CustomerFilters {
  return filters ?? DEFAULT_CUSTOMER_FILTERS;
}

function appendFilterParams(
  params: URLSearchParams,
  filters: CustomerFilters,
): void {
  [...filters.tags].sort().forEach((tag) => {
    params.append('tags[]', tag);
  });

  [...filters.yearsOfBirth].sort().forEach((year) => {
    params.append('yearsOfBirth[]', year);
  });

  [...filters.months]
    .sort((left, right) => Number(left) - Number(right))
    .forEach((month) => {
      params.append('month[]', month);
    });

  if (filters.marketingStatus)
    params.set('marketingStatus', filters.marketingStatus);

  if (filters.includeMail) {
    params.set('includeMail', 'true');
  }

  if (filters.includeCustomersWithMarketingConsent) {
    params.set('includeCustomersWithMarketingConsent', 'true');
  }

  if (filters.isInBlackList) {
    params.set('isInBlackList', 'true');
  }

  if (filters.phonePrefix) {
    params.set('prefix', filters.phonePrefix);
  }

  if (filters.minimumBookings) {
    params.set(NUMBERS_OF_BOOKINGS_PARAM, filters.minimumBookings);
  }

  if (filters.minEdgeBookingPeriod) {
    params.set(MIN_EDGE_BOOKING_PERIOD_PARAM, filters.minEdgeBookingPeriod);
  }

  if (filters.maxEdgeBookingPeriod) {
    params.set(MAX_EDGE_BOOKING_PERIOD_PARAM, filters.maxEdgeBookingPeriod);
  }

  if (filters.formCode) {
    params.set(FORM_CODE_PARAM, filters.formCode);
  }
}

function buildCustomersUrl({
  branchCode,
  page = 0,
  size = 20,
  search,
  filters,
}: CustomerListParams): string {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));

  if (search?.trim()) {
    params.set('search', search.trim());
  }

  appendFilterParams(params, resolveFilters(filters));

  return `/branches/${branchCode}/customers?${params.toString()}`;
}

function filtersChanged(
  current?: CustomerFilters,
  previous?: CustomerFilters,
): boolean {
  return (
    buildCustomerFiltersCacheKey(resolveFilters(current)) !==
    buildCustomerFiltersCacheKey(resolveFilters(previous))
  );
}

export const customersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    customerMarketingStatus: builder.query<
      { marketingStatus: string | null; updatedAt?: string },
      { branch: string; customerId: number }
    >({
      query: ({ branch, customerId }) =>
        `/branches/${branch}/customers/${String(customerId)}/marketing-status`,
      providesTags: (_r, _e, { branch }) => [
        { type: API_TAGS.Customers, id: branch },
      ],
    }),
    updateCustomerMarketingStatus: builder.mutation<
      { marketingStatus: string | null },
      { branch: string; customerId: number; marketingStatus: string | null }
    >({
      query: ({ branch, customerId, marketingStatus }) => ({
        url: `/branches/${branch}/customers/${String(customerId)}/marketing-status`,
        method: 'PUT',
        body: { marketingStatus },
      }),
      invalidatesTags: (_r, _e, { branch }) => [
        { type: API_TAGS.Customers, id: branch },
      ],
    }),
    getCustomers: builder.query<
      PaginatedResponse<Customer>,
      CustomerListParams
    >({
      query: (args) => ({
        url: buildCustomersUrl(args),
        method: 'GET',
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const {
          branchCode,
          page = 0,
          size = 20,
          search = '',
          filtersKey,
        } = queryArgs;
        const resolvedFiltersKey =
          filtersKey ??
          buildCustomerFiltersCacheKey(resolveFilters(queryArgs.filters));
        return `${endpointName}(${branchCode}|${String(page)}|${String(size)}|${search}|${resolvedFiltersKey})`;
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        if (!previousArg || !currentArg) {
          return true;
        }
        return (
          currentArg.search !== previousArg.search ||
          currentArg.page !== previousArg.page ||
          currentArg.size !== previousArg.size ||
          currentArg.branchCode !== previousArg.branchCode ||
          filtersChanged(currentArg.filters, previousArg.filters)
        );
      },
      transformResponse: (
        response:
          | Pro20ApiResponse<Pro20Page<Customer> | Customer[]>
          | Pro20Page<Customer>
          | Customer[],
        _meta,
        arg,
      ) => {
        const results = normalizePro20Response(response);
        return normalizeCustomersResponse(results, arg.size ?? 20);
      },
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    updateCustomer: builder.mutation<Customer, UpdateCustomerPayload>({
      query: (body) => ({
        url: '/customer/updatecustomer',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as Customer,
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    createCustomer: builder.mutation<Customer, CreateCustomerPayload>({
      query: (body) => ({
        url: '/customer/save',
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as Customer,
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    exportCustomers: builder.query<CustomerExportResult, CustomerExportParams>({
      query: ({
        branchCode,
        search,
        sortBy = 'lastName',
        sortDirection = 'asc',
        filters,
      }) => {
        const params = new URLSearchParams();

        if (search?.trim()) {
          params.set('search', search.trim());
        }

        params.set('sortBy', sortBy);
        params.set('sortDirection', sortDirection);
        appendFilterParams(params, resolveFilters(filters));

        return {
          url: `/branches/${encodeURIComponent(branchCode)}/customers/export?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as CustomerExportResult,
    }),
    getCustomerImportStatus: builder.query<
      CustomerImportResult,
      { branchCode: string; importId: string }
    >({
      query: ({ branchCode, importId }) =>
        `/customer/importcustomers/${encodeURIComponent(branchCode)}/${encodeURIComponent(importId)}`,
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as CustomerImportResult,
    }),
    importCustomers: builder.mutation<
      CustomerImportResult,
      CustomerImportParams
    >({
      query: ({ branchCode, file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: `/customer/importcustomers/${encodeURIComponent(branchCode)}`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: unknown) =>
        normalizePro20Response(response) as CustomerImportResult,
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Customers, id: branchCode },
      ],
    }),
    getCustomerById: builder.query<Customer, GetCustomerByIdParams>({
      query: ({ branchCode, customerId }) => ({
        url: `/branches/${encodeURIComponent(branchCode)}/customers/${encodeURIComponent(String(customerId))}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<Customer> | Customer) =>
        normalizePro20Response(response),
      providesTags: (_result, _error, { branchCode, customerId }) => [
        { type: API_TAGS.Customers, id: `${branchCode}-${String(customerId)}` },
      ],
    }),
    searchCustomersByQuery: builder.query<Customer[], CustomerSearchParams>({
      query: ({ branchCode, query, page = 0, size = 20 }) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('size', String(size));
        params.set('query', query.trim());

        return {
          url: `/customer/getcustomers/${encodeURIComponent(branchCode)}?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (
        response:
          | Pro20ApiResponse<Pro20Page<Customer> | Customer[]>
          | Pro20Page<Customer>
          | Customer[],
      ) => {
        const results = normalizePro20Response(response);

        if (Array.isArray(results)) {
          return results;
        }

        if (typeof results === 'object' && Array.isArray(results.content)) {
          return results.content;
        }

        return [];
      },
    }),
    deleteCustomerByCode: builder.mutation<null, DeleteCustomerByCodeParams>({
      query: ({ customerCode }) => ({
        url: `/customer/deletebycode/${encodeURIComponent(customerCode)}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode
          ? [{ type: API_TAGS.Customers, id: branchCode }]
          : [{ type: API_TAGS.Customers, id: 'LIST' }],
    }),
  }),
});

export const {
  useCustomerMarketingStatusQuery,
  useUpdateCustomerMarketingStatusMutation,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useUpdateCustomerMutation,
  useCreateCustomerMutation,
  useLazyExportCustomersQuery,
  useImportCustomersMutation,
  useGetCustomerImportStatusQuery,
  useGetCustomerByIdQuery,
  useSearchCustomersByQueryQuery,
  useDeleteCustomerByCodeMutation,
} = customersApi;
